import type { Dispatch, SetStateAction } from 'react';
import { label, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import type { useCreateRequestMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Row, Small } from '../styles';
import { choice, dateField, textField } from '../types';
import { AlertDeliveries } from '../components/AlertDeliveries';
import { PagedTable } from '../components/CollectionTable';

export interface MilestonesTabProps {
  workspace: KspWorkspace;
  projectId: string;
  timezone: string;
  canWrite: boolean;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  fail: (error: unknown) => void;
  setMessage: (msg: string) => void;
  fileOptions: { value: string; label: string }[];
  letterOptions: { value: string; label: string }[];
  evidenceOptions: { value: string; label: string }[];
  registerDispatchEvidence: (letterId: string) => void;
  createFunctionRequest: ReturnType<typeof useCreateRequestMutation>[0];
  creatingReq: boolean;
  preparedMessages: Record<string, string>;
  setPreparedMessages: Dispatch<SetStateAction<Record<string, string>>>;
}

export function MilestonesTab({
  workspace: d,
  projectId,
  timezone,
  canWrite,
  permission,
  open,
  execute,
  fail,
  setMessage,
  fileOptions,
  letterOptions,
  evidenceOptions,
  registerDispatchEvidence,
  createFunctionRequest,
  creatingReq,
  preparedMessages,
  setPreparedMessages,
}: MilestonesTabProps) {
  return (
    <>
      <Card>
        <Row>
          <h3>Договорные вехи</h3>
          <Button
            disabled={!permission()}
            onClick={() =>
              open(
                'Новая веха',
                [
                  textField('code', 'Код вехи'),
                  textField('name', 'Название'),
                  dateField('deadline_date', 'Договорная дата'),
                  textField('basis', 'Критерии достижения и основание'),
                  textField('responsible_id', 'Ответственный', false),
                ],
                (values) => execute(`projects/${projectId}/milestones`, values),
              )
            }
          >
            Добавить веху
          </Button>
        </Row>
        <PagedTable
          rows={d.milestones}
          columns={[
            { name: 'Веха', value: (milestone) => milestone.title },
            {
              name: 'Договорная дата',
              value: (milestone) => projectDate(milestone.contractual_due_at, timezone),
            },
            {
              name: 'Прогноз',
              value: (milestone) => projectDate(milestone.forecast_finish, timezone),
            },
            { name: 'Достижение', value: (milestone) => label(milestone.achievement_state) },
            {
              name: 'Действие',
              value: (milestone) => (
                <Row>
                  <Button
                    $size="sm"
                    disabled={!permission('approve')}
                    onClick={() =>
                      open(
                        'Новая ревизия срока вехи',
                        [
                          dateField('deadline_date', 'Новая договорная дата'),
                          textField('basis', 'Документальное основание'),
                          choice('approval_file_id', 'Согласование Заказчика', fileOptions),
                        ],
                        (values) => execute(`milestones/${milestone.id}/revise`, values),
                      )
                    }
                  >
                    Перенести по основанию
                  </Button>
                  <Button
                    $size="sm"
                    disabled={!permission('approve')}
                    onClick={() =>
                      open(
                        'Подтвердить достижение',
                        [
                          textField('basis', 'Критерии выполнены'),
                          choice(
                            'achievement_file_id',
                            'Подтверждающий документ',
                            fileOptions,
                          ),
                        ],
                        (values) =>
                          execute(
                            `milestones/${milestone.revision_id ?? milestone.id}/achievement`,
                            { ...values, achievement: 'verified' },
                          ),
                      )
                    }
                  >
                    Достижение
                  </Button>
                  <Button
                    $size="sm"
                    disabled={!permission('letter.send') || !milestone.revision_id}
                    onClick={() =>
                      open(
                        'Письмо по сроку вехи',
                        [
                          textField(
                            'sender_name',
                            'Организация-отправитель',
                            true,
                            d.settings.sender,
                          ),
                          textField('customer_name', 'Заказчик', true, d.settings.customer),
                          textField('letter_no', 'Исходящий номер'),
                          dateField('letter_date', 'Дата письма'),
                          {
                            ...textField('reasons', 'Изложенные причины и мероприятия'),
                            type: 'textarea',
                          },
                          choice('source_file_id', 'Файл письма', fileOptions),
                        ],
                        (values) =>
                          execute(
                            `milestone-revisions/${milestone.revision_id}/letter-links`,
                            values,
                          ),
                      )
                    }
                  >
                    Письмо по сроку
                  </Button>
                  <Button
                    $size="sm"
                    disabled={!permission('alert.close') || !milestone.revision_id}
                    onClick={() => {
                      const letters =
                        d.letters?.filter(
                          (letter) => letter.milestone_revision_id === milestone.revision_id,
                        ) ?? [];
                      const letterIds = new Set(letters.map((letter) => letter.id));
                      open(
                        'Принять ранее направленное письмо',
                        [
                          choice(
                            'letter_id',
                            'Письмо по этой ревизии вехи',
                            letters.map((letter) => ({
                              value: letter.id,
                              label: letter.letter_no ?? letter.id,
                            })),
                          ),
                          choice(
                            'evidence_id',
                            'Проверенная отправка письма',
                            (d.evidence ?? [])
                              .filter(
                                (evidence) =>
                                  !evidence.revoked_at && letterIds.has(evidence.letter_id),
                              )
                              .map((evidence) => ({
                                value: evidence.id,
                                label: `${evidence.channel} · ${evidence.reference}`,
                              })),
                          ),
                          textField(
                            'comment',
                            'Решение специалиста и основание предварительного закрытия',
                          ),
                        ],
                        (values) =>
                          execute(
                            `milestone-revisions/${milestone.revision_id}/close-by-letter`,
                            values,
                          ),
                      );
                    }}
                  >
                    Принять ранее направленное письмо
                  </Button>
                </Row>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Опасность просрочки Вехи</h3>
        <Muted>
          Сообщение начинается за 14 календарных дней до договорной даты и продолжается
          ежедневно до закрытия по направленному Заказчику письму. Прогноз и достижение
          показываются отдельно.
        </Muted>
        <Small>
          Ранее направленное письмо можно связать с ревизией вехи и принять отдельным решением
          специалиста до начала окна предупреждений. Создание или отправка письма сами по себе
          предупреждение не закрывают.
        </Small>
        {d.alerts.map((alert) => (
          <Card key={alert.id}>
            <Row>
              <strong>{alert.title || 'Опасность просрочки Вехи'}</strong>
              <span>{label(alert.status)}</span>
            </Row>
            <Small>
              Открыто {projectDate(alert.opened_at, timezone)} · окно {alert.trigger_date}
            </Small>
            <AlertDeliveries id={alert.id} />
            <Row>
              <Button
                disabled={!permission('letter.send') || alert.status !== 'open'}
                onClick={() =>
                  open(
                    'Связать исходящее письмо с вехой',
                    [
                      textField(
                        'sender_name',
                        'Организация-отправитель',
                        true,
                        d.settings.sender,
                      ),
                      textField('customer_name', 'Заказчик', true, d.settings.customer),
                      textField('letter_no', 'Исходящий номер'),
                      dateField('letter_date', 'Дата письма'),
                      {
                        ...textField('reasons', 'Изложенные причины и мероприятия'),
                        type: 'textarea',
                      },
                      choice('source_file_id', 'Файл письма', fileOptions),
                    ],
                    (values) =>
                      execute(`alerts/${alert.id}/letter-links`, {
                        ...values,
                        milestone_revision_id: alert.milestone_id,
                      }),
                  )
                }
              >
                Письмо по вехе
              </Button>
              <Button
                disabled={!permission('evidence.verify') || alert.status !== 'open'}
                onClick={() =>
                  open(
                    'Зарегистрировать доказательство направления',
                    [
                      choice('letter_id', 'Зарегистрированное письмо', letterOptions),
                      choice('channel', 'Канал направления', [
                        { value: 'smtp', label: 'Почта' },
                        { value: 'edi', label: 'ЭДО' },
                        { value: 'manual', label: 'Документальная отправка' },
                      ]),
                      {
                        ...textField('dispatched_at', 'Время направления UTC'),
                        type: 'datetime-local',
                      },
                      textField('reference', 'Идентификатор отправки'),
                      textField('message_id', 'Message-ID почтового отправления', false),
                      textField('recipients', 'Адресаты Заказчика, через запятую'),
                      choice('evidence_file_id', 'Подтверждающий файл', fileOptions),
                      textField('verification_note', 'Результат сверки'),
                    ],
                    (values) =>
                      execute(`letters/${values.letter_id}/dispatch-evidence`, {
                        ...values,
                        dispatched_at: new Date(`${values.dispatched_at}:00Z`).toISOString(),
                        recipients: values.recipients.split(',').map((value) => value.trim()),
                      }),
                  )
                }
              >
                Доказательство направления
              </Button>
              <Button
                disabled={!permission('alert.close') || alert.status !== 'open'}
                onClick={() =>
                  open(
                    'Закрыть по письму',
                    [
                      choice(
                        'letter_link_id',
                        'Зарегистрированное письмо',
                        letterOptions,
                        alert.letter_link_id,
                      ),
                      choice(
                        'dispatch_evidence_id',
                        'Подтверждённая отправка',
                        evidenceOptions,
                        alert.dispatch_evidence_id,
                      ),
                      textField(
                        'comment',
                        'Причины изложены в направленном Заказчику письме',
                      ),
                    ],
                    (values) =>
                      execute(`alerts/${alert.id}/close-by-letter`, {
                        ...values,
                        alert_expected_version: alert.version,
                      }),
                  )
                }
              >
                Закрыть по письму
              </Button>
              <Button
                disabled={!canWrite || creatingReq}
                onClick={() =>
                  open(
                    'Заявка в смежный отдел по риску вехи',
                    [
                      choice(
                        'target_agent',
                        'Отдел-исполнитель',
                        [
                          { value: 'statut', label: 'Договорной (Статут)' },
                          { value: 'mto', label: 'Снабжение (МТО)' },
                          { value: 'pto', label: 'ПТО' },
                          { value: 'smeta', label: 'Сметный отдел' },
                        ],
                        'statut',
                      ),
                      choice(
                        'priority',
                        'Приоритет',
                        [
                          { value: 'high', label: 'Высокий' },
                          { value: 'urgent', label: 'Критический' },
                          { value: 'normal', label: 'Обычный' },
                        ],
                        'high',
                      ),
                      textField(
                        'title',
                        'Заголовок заявки',
                        true,
                        `Риск просрочки вехи: ${alert.title || alert.id}`,
                      ),
                      {
                        ...textField(
                          'description',
                          'Описание и требуемые действия',
                          true,
                          `Предупреждение КСП от ${projectDate(alert.opened_at, timezone)} (окно ${alert.trigger_date}). Требуется проработка причин/уведомления заказчика.`,
                        ),
                        type: 'textarea',
                      },
                    ],
                    async (values) => {
                      try {
                        await createFunctionRequest({
                          source_function: 'ksp',
                          request_type: 'milestone_delay_alert',
                          title:
                            values.title || `Риск просрочки вехи: ${alert.title || alert.id}`,
                          priority: (values.priority || 'high') as
                            | 'low'
                            | 'normal'
                            | 'high'
                            | 'urgent',
                          project_id: projectId,
                          payload: {
                            target_agent: values.target_agent || 'statut',
                            description: values.description || undefined,
                            alert_id: alert.id,
                            milestone_id: alert.milestone_id,
                            trigger_date: alert.trigger_date,
                          },
                          submit: true,
                        }).unwrap();
                        setMessage('Заявка в смежный отдел создана и отправлена.');
                      } catch (e) {
                        fail(e);
                      }
                    },
                  )
                }
              >
                В заявку отделу
              </Button>
            </Row>
            {alert.closed_at && (
              <Small>
                Закрыто {projectDate(alert.closed_at, timezone)} · письмо{' '}
                {alert.letter_link_id} · доказательство {alert.dispatch_evidence_id}
              </Small>
            )}
          </Card>
        ))}
      </Card>
      <Card>
        <h3>Исходящие письма по вехам</h3>
        {d.letters?.map((letter) => (
          <Card key={letter.id}>
            <h4>Письмо {letter.letter_no ?? 'без номера'}</h4>
            <Button
              $variant="secondary"
              disabled={!permission('evidence.verify')}
              onClick={() => registerDispatchEvidence(letter.id)}
            >
              Проверить доказательство отправки
            </Button>
            <Muted>{letter.reasons}</Muted>
            <Small>
              Приложение:{' '}
              {d.source_files?.find((file) => file.id === letter.source_file_id)?.filename ??
                letter.source_file_id}
            </Small>
            <Row>
              <Button
                disabled={!permission('letter.send')}
                onClick={() =>
                  open(
                    'Подготовить направление письма Заказчику',
                    [
                      textField('subject', 'Тема письма'),
                      textField('recipient', 'Адрес Заказчика'),
                    ],
                    async (values) => {
                      const saved = await execute(`letters/${letter.id}/prepare`, {
                        subject: values.subject,
                        recipients: [values.recipient],
                      });
                      const messageId = (saved as { id?: string })?.id;
                      if (typeof messageId === 'string')
                        setPreparedMessages((previous) => ({
                          ...previous,
                          [letter.id]: messageId,
                        }));
                    },
                  )
                }
              >
                Подготовить направление
              </Button>
              <Button
                disabled={
                  !permission('letter.send') ||
                  !(
                    preparedMessages[letter.id] ??
                    d.outgoing_messages?.find((message) => message.letter_id === letter.id)
                      ?.id
                  )
                }
                onClick={() =>
                  open(
                    'Направить подготовленное письмо Заказчику',
                    [textField('comment', 'Основание направления')],
                    (values) =>
                      execute(`letters/${letter.id}/send`, {
                        ...values,
                        outgoing_message_id:
                          preparedMessages[letter.id] ??
                          d.outgoing_messages?.find(
                            (message) => message.letter_id === letter.id,
                          )?.id,
                      }),
                    'Направить письмо',
                  )
                }
              >
                Направить письмо
              </Button>
            </Row>
            <Small>
              Подготовленный файл, адресат и идентификатор отправки фиксируются сервером.
              Неопределённый результат требует сверки перед повтором.
            </Small>
          </Card>
        ))}
      </Card>
    </>
  );
}
